import api from '@/core/api/client';

export type BillingType = 'RECURRING' | 'ONE_TIME';

export interface Product {
  id: string;
  name: string;
  description?: string | null;
  price: number;
  billingType: BillingType;
  featureKey?: string | null;
  amount: number;
  isModule: boolean;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  /** ID do Product no Stripe (preenchido após a primeira sync). */
  stripeProductId?: string | null;
  /** Quantas linhas de product_prices este produto tem. */
  pricesCount?: number;
  /** Quantas dessas linhas já foram materializadas como Stripe Price. */
  syncedPricesCount?: number;
  /** Moeda da linha default em product_prices (ex.: 'BRL'). */
  defaultCurrency?: string | null;
  /** Valor (em menor unidade) da moeda default. */
  defaultUnitAmount?: number | null;
}

export interface ProductsListParams {
  page?: number;
  limit?: number;
  search?: string;
  active?: boolean;
  billingType?: BillingType;
  featureKey?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface CreateProductData {
  name: string;
  description?: string;
  price: number;
  billingType?: BillingType;
  featureKey?: string;
  amount?: number;
  isModule?: boolean;
}

export interface UpdateProductData {
  name?: string;
  description?: string;
  price?: number;
  billingType?: BillingType;
  featureKey?: string;
  amount?: number;
  isModule?: boolean;
  active?: boolean;
}

export const productsService = {
  async list(params: ProductsListParams = {}): Promise<PaginatedResponse<Product>> {
    const response = await api.get('/products', { params });
    if (response.data.success) {
      return {
        data: response.data.data,
        pagination: response.data.pagination,
      };
    }
    throw new Error(response.data.message || 'Erro ao listar produtos');
  },

  async get(id: string): Promise<Product> {
    const response = await api.get(`/products/${id}`);
    if (response.data.success) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Erro ao buscar produto');
  },

  async create(data: CreateProductData): Promise<Product> {
    const response = await api.post('/products', data);
    if (response.data.success) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Erro ao criar produto');
  },

  async update(id: string, data: UpdateProductData): Promise<Product> {
    const response = await api.put(`/products/${id}`, data);
    if (response.data.success) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Erro ao atualizar produto');
  },

  async delete(id: string): Promise<void> {
    const response = await api.delete(`/products/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Erro ao deletar produto');
    }
  },

  async listPrices(productId: string): Promise<ProductPriceRow[]> {
    const response = await api.get(`/products/${productId}/prices`);
    if (response.data?.success) return response.data.data as ProductPriceRow[];
    throw new Error(response.data?.message || 'Erro ao listar preços');
  },

  async upsertPrice(productId: string, body: UpsertProductPriceData): Promise<ProductPriceRow> {
    const response = await api.put(`/products/${productId}/prices`, body);
    if (response.data?.success) return response.data.data as ProductPriceRow;
    throw new Error(response.data?.message || 'Erro ao salvar preço');
  },

  async deletePrice(productId: string, priceId: string): Promise<void> {
    const response = await api.delete(`/products/${productId}/prices/${priceId}`);
    if (!response.data?.success) {
      throw new Error(response.data?.message || 'Erro ao remover preço');
    }
  },

  /**
   * Enfileira uma sincronização manual deste produto com o Stripe. O backend
   * apenas insere um job UPSERT em `billing_sync_outbox` e responde 202; o worker
   * (`StripeCatalogSyncWorker`, polling a cada 5s) materializa Product + Prices
   * e atualiza `stripePriceId` nas linhas correspondentes.
   */
  async syncStripe(productId: string): Promise<void> {
    const response = await api.post(`/products/${productId}/sync-stripe`);
    if (!response.data?.success) {
      throw new Error(response.data?.message || 'Erro ao sincronizar com Stripe');
    }
  },
};

export interface ProductPriceRow {
  id: string;
  productId: string;
  currency: string;
  unitAmount: number;
  stripePriceId: string | null;
  stripePricesHash: string | null;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
  syncStatus: 'SYNCED' | 'PENDING';
}

export interface UpsertProductPriceData {
  currency: string;
  unitAmount: number;
  isDefault?: boolean;
}
